import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Draggable from "react-draggable";
import ReactDOM from "react-dom";
import {
  IMAGE_WIDTH,
  MOBILE_MAX_WIDTH,
  clampToViewport,
  getIsMobile,
  readStoredExpanded,
  readStoredPos,
  writeStoredExpanded,
  writeStoredPos,
} from "./DraggableCard.helpers";

/**
 * DraggableCard (generic)
 * - Renders any content in a draggable card.
 * - Optional position persistence using localStorage (storageKey).
 * - Optional portal rendering to escape stacking contexts (usePortal).
 * - Optional bounds (react-draggable bounds).
 * - Optional callbacks (onPositionChange).
 */
export default function DraggableCard({
  storageKey,
  defaultPosition,
  title,
  width,
  children,
  handleSelector,
  bounds,
  zIndex,
  usePortal,
  positionMode,
  onPositionChange,
  defaultExpanded,
  persistExpanded,
  onExpandedChange,
  screenWidth,
  zoomEnabled,
}) {
  const canUseDOM = typeof window !== "undefined" && typeof document !== "undefined";
  const nodeRef = React.useRef(null);
  const panelScale = zoomEnabled ? 1 : (screenWidth ? (screenWidth / IMAGE_WIDTH) : 1);

  const [isMobile, setIsMobile] = React.useState(() => (canUseDOM ? getIsMobile() : false));

  React.useEffect(() => {
    if (!canUseDOM) return undefined;
    const mql = window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const onChange = () => {
      setIsMobile(mql.matches);
    };
    setIsMobile(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => {
        mql.removeEventListener("change", onChange);
      };
    }
    const legacyMql = /** @type {any} */ (mql);
    legacyMql.addListener(onChange);
    return () => {
      legacyMql.removeListener(onChange);
    };
  }, [canUseDOM]);

  const VIEWPORT_PADDING = {
    left: 94, right: 8, top: 8, bottom: 8
  };

  const [cardSize, setCardSize] = React.useState({ w: 250, h: 120 });

  const measureCard = React.useCallback(() => {
    if (!canUseDOM) return;
    if (!nodeRef.current) return;
    const rect = nodeRef.current.getBoundingClientRect();
    const s = panelScale && panelScale > 0 ? panelScale : 1;
    const w = rect.width / s;
    const h = rect.height / s;
    if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
      setCardSize({ w, h });
    }
  }, [canUseDOM, panelScale]);

  const clampPos = React.useCallback((nextPos) => {
    if (!canUseDOM) return nextPos;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    return clampToViewport(
      nextPos,
      vw,
      vh,
      cardSize.w,
      cardSize.h,
      panelScale,
      VIEWPORT_PADDING
    );
  }, [canUseDOM, cardSize.w, cardSize.h, panelScale]);

  const [pos, setPos] = React.useState(() => {
    if (!canUseDOM) return defaultPosition;
    const stored = readStoredPos(storageKey);
    return stored || defaultPosition;
  });

  const [expanded, setExpanded] = React.useState(() => {
    if (!canUseDOM) return defaultExpanded;
    if (!persistExpanded) return defaultExpanded;
    return readStoredExpanded(storageKey, defaultExpanded);
  });

  // If storageKey changes, reload saved position + expanded state
  React.useEffect(() => {
    if (!canUseDOM) return;

    const stored = readStoredPos(storageKey);

    if (stored) setPos(clampPos(stored));
    else setPos(clampPos(defaultPosition));

    if (persistExpanded) {
      setExpanded(readStoredExpanded(storageKey, defaultExpanded));
    } else {
      setExpanded(defaultExpanded);
    }
  }, [canUseDOM, storageKey, persistExpanded, defaultExpanded, defaultPosition, clampPos]);

  React.useEffect(() => {
    if (!canUseDOM) return undefined;
    const t = setTimeout(() => measureCard(), 0);
    return () => clearTimeout(t);
  }, [canUseDOM, expanded, panelScale, width, measureCard]);

  React.useEffect(() => {
    if (!canUseDOM) return undefined;
    const onResize = () => {
      measureCard();
      setPos((prev) => clampPos(prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [canUseDOM, clampPos, measureCard]);

  const commitPos = React.useCallback(
    (next) => {
      const clamped = clampPos(next);
      setPos(clamped);
      writeStoredPos(storageKey, clamped);
      if (onPositionChange) onPositionChange(clamped);
    },
    [storageKey, onPositionChange, clampPos]
  );

  const handleDrag = React.useCallback(
    (e, data) => {
      const next = clampPos({ x: data.x, y: data.y });
      setPos(next);
      if (onPositionChange) onPositionChange(next);
    },
    [onPositionChange, clampPos]
  );

  const handleStop = React.useCallback(
    (e, data) => {
      commitPos({ x: data.x, y: data.y });
    },
    [commitPos]
  );

  const toggleExpanded = React.useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (persistExpanded) writeStoredExpanded(storageKey, next);
      if (onExpandedChange) onExpandedChange(next);
      return next;
    });
    if (canUseDOM) {
      setTimeout(() => {
        measureCard();
        setPos((prev) => clampPos(prev));
      }, 0);
    }
  }, [persistExpanded, storageKey, onExpandedChange]);

  const content = (
    <Wrapper ref={nodeRef} $width={width} $zIndex={zIndex} $positionMode={positionMode}>
      <Header className={handleSelector.startsWith(".") ? handleSelector.slice(1) : undefined}>
        <HeaderTitle title={title}>{title}</HeaderTitle>
        <HeaderActions>
          <IconButton
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleExpanded();
            }}
            aria-label={expanded ? "Colapsar" : "Expandir"}
            title={expanded ? "Colapsar" : "Expandir"}
          >
            {expanded ? "▲" : "▼"}
          </IconButton>
        </HeaderActions>
      </Header>
      {expanded ? <Body>{children}</Body> : null}
    </Wrapper>
  );

  const card = (
    <ScaleLayer $scale={panelScale} $zIndex={zIndex}>
      <Draggable
        nodeRef={nodeRef}
        handle={handleSelector}
        scale={panelScale}
        bounds={bounds}
        position={pos}
        onDrag={handleDrag}
        onStop={handleStop}
      >
        {content}
      </Draggable>
    </ScaleLayer>
  );

  if (!canUseDOM || isMobile) return content;

  return usePortal ? ReactDOM.createPortal(card, document.body) : card;
}

DraggableCard.propTypes = {
  storageKey: PropTypes.string,
  defaultPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  }),
  title: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  defaultExpanded: PropTypes.bool,
  persistExpanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
  screenWidth: PropTypes.number,
  zoomEnabled: PropTypes.bool,
  // react-draggable options
  handleSelector: PropTypes.string,
  bounds: PropTypes.oneOfType([
    PropTypes.string, // "parent"
    PropTypes.shape({
      left: PropTypes.number,
      right: PropTypes.number,
      top: PropTypes.number,
      bottom: PropTypes.number,
    }),
  ]),

  // rendering/options
  zIndex: PropTypes.number,
  usePortal: PropTypes.bool,
  positionMode: PropTypes.oneOf(["fixed", "absolute"]),

  // callbacks
  onPositionChange: PropTypes.func,
};

DraggableCard.defaultProps = {
  storageKey: null,
  defaultPosition: { x: 120, y: 24 },
  title: "Card",
  width: 360,
  children: null,

  handleSelector: ".drag-handle",
  bounds: undefined,

  zIndex: 1000,
  usePortal: true,
  positionMode: "fixed",

  onPositionChange: null,
  defaultExpanded: true,
  persistExpanded: true,
  onExpandedChange: null,
  screenWidth: IMAGE_WIDTH,
  zoomEnabled: true,
};

const Wrapper = styled.div`
  width: ${(p) => (typeof p.$width === "number" ? `${p.$width}px` : p.$width)};
  max-width: 250px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.12);
  user-select: none;
  position: ${(p) => p.$positionMode};
  top: 0;
  left: 0;
  z-index: 0;
  @media screen and (max-width: 800px) {
    position: relative;
    top: auto;
    left: auto;
    margin-left: 14px;
    margin-top: 54px;
}`;

const ScaleLayer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${(p) => p.$zIndex};
  transform: ${(p) => (p.$scale && p.$scale !== 1 ? `scale(${p.$scale})` : "none")};
  transform-origin: 0 0;

  @media screen and (max-width: 800px) {
    transform: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  cursor: move;
`;

const HeaderTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  font-family: Montserrat, Helvetica Neue, Arial, sans-serif;
  line-height: 21px;
  @media screen and (max-width: 800px) {
    font-weight: 700;
    font-size: 14px;
    font-family: Montserrat, Helvetica Neue, Arial, sans-serif;
    line-height: 21px;
  }

  // max-width: 260px;
  // overflow: hidden;
  // white-space: nowrap;
  // text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const IconButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  color: #2E384D
  opacity: 0.75;
  border-radius: 6px;

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`;

const Body = styled.div`
  padding: 12px;
`;
